import { useEffect, useState } from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';
import { useAdminUsersStore } from '../../store/adminUsersStore';
import type { User, UserRole } from '../../types';
import { UserEditPanel } from './UserEditPanel';

const ROLE_LABELS: Record<UserRole, string> = {
  ADMIN: 'Admin',
  MANAGER: 'Manager',
  INSIDE_SALES: 'Inside Sales',
  SALES_REP: 'Sales Rep',
  MATCHING_SUPPORT: 'Matching Support',
  DATA_STEWARD: 'Data Steward',
  CAMPAIGN_MANAGER: 'Campaign Manager',
  ASSISTANT: 'Assistant',
};

export function UserList() {
  const { users, loading, filters, setFilter, fetchUsers } = useAdminUsersStore();
  const [selected, setSelected] = useState<User | null>(null);

  useEffect(() => {
    void fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the selected user's snapshot in sync after store mutations (so
  // toggling a role checkbox updates the panel without re-opening it).
  useEffect(() => {
    if (!selected) return;
    const fresh = users.find((u) => u.id === selected.id);
    if (fresh && fresh !== selected) setSelected(fresh);
  }, [users, selected]);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          className="rounded border border-gray-300 px-3 py-1.5 text-sm"
          placeholder="Search by name or email"
          value={filters.search}
          onChange={(e) => setFilter('search', e.target.value)}
        />
        <select
          className="rounded border border-gray-300 px-3 py-1.5 text-sm"
          value={filters.role}
          onChange={(e) => setFilter('role', e.target.value)}
        >
          <option value="all">All roles</option>
          {(Object.keys(ROLE_LABELS) as UserRole[]).map((r) => (
            <option key={r} value={r}>
              {ROLE_LABELS[r]}
            </option>
          ))}
        </select>
        <select
          className="rounded border border-gray-300 px-3 py-1.5 text-sm"
          value={filters.isActive}
          onChange={(e) => setFilter('isActive', e.target.value)}
        >
          <option value="all">All users</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Email</th>
              <th className="px-4 py-2">Roles</th>
              <th className="px-4 py-2">Active</th>
              <th className="px-4 py-2">Exempt</th>
              <th className="px-4 py-2">Last login</th>
              <th className="px-4 py-2 text-right">Assigned</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && users.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-gray-500">
                  No users match.
                </td>
              </tr>
            )}
            {users.map((u) => (
              <tr
                key={u.id}
                onClick={() => setSelected(u)}
                className="cursor-pointer hover:bg-gray-50"
              >
                <td className="px-4 py-2">
                  <div>{u.displayName ?? '—'}</div>
                  {u.worksFor && (
                    <div className="text-xs text-gray-500">
                      Assistant to {u.worksFor.displayName ?? 'Unnamed rep'}
                    </div>
                  )}
                </td>
                <td className="px-4 py-2 text-gray-600">{u.email}</td>
                <td className="px-4 py-2">
                  <div className="flex flex-wrap gap-1">
                    {u.roles.map((r) => (
                      <span
                        key={r}
                        className="rounded bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"
                      >
                        {ROLE_LABELS[r]}
                      </span>
                    ))}
                    {u.roles.length === 0 && (
                      <span className="text-gray-400">none</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-2">
                  {u.isActive ? (
                    <CheckCircle2 size={16} className="text-green-600" />
                  ) : (
                    <XCircle size={16} className="text-gray-400" />
                  )}
                </td>
                <td className="px-4 py-2">
                  {u.sweepExempt ? (
                    <CheckCircle2 size={16} className="text-green-600" />
                  ) : (
                    <XCircle size={16} className="text-gray-400" />
                  )}
                </td>
                <td className="px-4 py-2 text-gray-600">
                  {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : 'never'}
                </td>
                <td className="px-4 py-2 text-right">{u.assignedPeopleCount ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && <UserEditPanel user={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
